/**
 * The realm of the deployment that answered this page (CLA-1829).
 *
 * `getRealm()` is the SIGNED-IN realm, from `/auth/me`. Booking,
 * manage-booking, consent and portal pages have no clinician session, and
 * claro.clinic fronts both realms, so they used to read AU words to US
 * patients. Every response now names its realm in `x-claro-realm`
 * (claro-elixir `ClaroWeb.Plugs.RealmHeader`); the fetchers note it here and
 * the copy helpers read `pageRealm()`.
 *
 * Its own module, not realm.ts, so tests that mock '@/lib/realm' keep
 * working: `pageRealm()` falls back to whatever `getRealm()` they mock.
 */
import { getRealm, type Realm } from '@/lib/realm'

/** Response header naming the deployment's realm (claro-elixir `ClaroWeb.Plugs.RealmHeader`). */
export const REALM_HEADER = 'x-claro-realm'

let served: Realm | null = null

/**
 * Note the realm a response came from. Kept for this page load only (not
 * persisted). A missing or unknown header is ignored, and this never throws.
 */
export function noteServedRealm(res: { headers?: Pick<Headers, 'get'> } | null | undefined): void {
  try {
    const v = res?.headers?.get?.(REALM_HEADER)
    if (v === 'au' || v === 'us') served = v
  } catch {
    /* a mocked or opaque response — keep what we had */
  }
}

/**
 * The realm for the words on screen: the realm of the deployment that has
 * answered this page, else the signed-in realm (`getRealm()`). A clinician's
 * session lives on their own realm's deployment, so in the app the two are
 * the same; on a signed-out public page only the first exists.
 */
export function pageRealm(): Realm {
  return served ?? getRealm()
}

/** Tests only: forget the served realm. */
export function resetServedRealm(): void {
  served = null
}
